// Page layouts for the Visual Editor: each page is a list of built-in sections plus content blocks the team adds.
import { HOME_SECTIONS, DISPLAY_FONTS, BODY_FONTS, withMissing } from "@/lib/design";

export const PAGE_SECTIONS = {
  home: HOME_SECTIONS,
  about: [["hero", "Top banner"], ["mission", "Our mission"], ["values", "Values cards"], ["faith", "What we believe"]],
  sermons: [["hero", "Top banner"], ["list", "Messages list & search"]],
  pastors: [["hero", "Top banner"], ["list", "Pastor cards"]],
  pastor: [["profile", "Photo, name & bio"], ["files", "Sermon files"], ["messages", "Messages by this pastor"]],
  events: [["hero", "Top banner"], ["departments", "Department cards"], ["other", "More photos & videos"]],
  dept: [["hero", "Top banner"], ["media", "Photos & videos (from the gallery)"]],
  donate: [["hero", "Top banner"], ["form", "Giving form"]],
  contact: [["hero", "Top banner"], ["email", "Email card"], ["info", "Phone, address & frequency"]],
  page: [["hero", "Top banner"], ["sections", "Page sections"]],
};

export const BLOCK_TYPES = [
  ["heading", "Heading"],
  ["text", "Text block"],
  ["announcement", "Announcement"],
  ["photo", "Photo"],
  ["slideshow", "Slideshow"],
  ["photos", "Photo gallery"],
  ["albums", "Photo albums"],
  ["video", "Video"],
  ["videos", "Video gallery"],
  ["divider", "Divider"],
];
export const BLOCK_LABEL = Object.fromEntries(BLOCK_TYPES);

const SIMPLE = { "/about": "about", "/sermons": "sermons", "/pastors": "pastors", "/events": "events", "/donate": "donate", "/contact": "contact" };
const NESTED = { pastors: "pastor", events: "dept", p: "page" };

export const kindOf = (pageKey = "") => pageKey.split(":")[0];

// "/events/youth" -> "dept:youth", "/about" -> "about", "/" -> "home"
export const pageKeyOf = (path = "/") => {
  const p = path.split("?")[0].replace(/\/+$/, "") || "/";
  if (p === "/") return "home";
  if (SIMPLE[p]) return SIMPLE[p];
  const [, a, b] = p.split("/");
  return NESTED[a] && b ? `${NESTED[a]}:${b}` : "";
};

// `order` gives the default order of built-in sections (the Home page keeps its own saved order).
export const layoutOf = (pageKey, saved, order) => {
  const builtins = order || (PAGE_SECTIONS[kindOf(pageKey)] || []).map(([k]) => k);
  const list = (Array.isArray(saved) ? saved : []).filter((b) => b && (b.type !== "section" || builtins.includes(b.key)));
  return withMissing(list, builtins, (b, k) => b.type === "section" && b.key === k, (k) => ({ id: `s-${k}`, type: "section", key: k }));
};

const TEXTY = new Set(["heading", "text", "announcement"]);

// Department pages: text and announcements go above the photos & videos; everything else goes to the end.
export const addBlock = (pageKey, list, type) => {
  const b = { id: `b-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, type };
  const media = list.findIndex((x) => x.type === "section" && x.key === "media");
  if (kindOf(pageKey) === "dept" && TEXTY.has(type) && media >= 0) return { block: b, list: [...list.slice(0, media), b, ...list.slice(media)] };
  return { block: b, list: [...list, b] };
};

// Field of a block for the current language: "" -> "en"/"mh", "title_" -> "title_en"/"title_mh".
export const blockField = (f, lang) => (f === "" ? lang : f.endsWith("_") ? `${f}${lang}` : f);

// Test id of a block on the page. "Select & remove" in the Visual Editor hides things by this id, so each page gets its own.
export const blockTestid = (pageKey, b) => (b.type === "section" ? `${pageKey.replace(/:/g, "-")}-section-${b.key}` : `block-${b.id}`);

export const TEXT_FONTS = [...DISPLAY_FONTS, ...BODY_FONTS];

// Inline look of a text block, from the controls in the "This page" tab.
export const blockStyle = (b) => {
  const s = {};
  if (b.size) s.fontSize = `${b.size}px`;
  if (TEXT_FONTS.includes(b.font)) s.fontFamily = `"${b.font}", serif`;
  if (b.color) s.color = b.color;
  if (b.bg) Object.assign(s, { backgroundColor: b.bg, padding: "1.25rem 1.5rem", borderRadius: "20px" });
  if (b.bold) s.fontWeight = 700;
  if (b.italic) s.fontStyle = "italic";
  if (b.underline) s.textDecoration = "underline";
  if (b.align) s.textAlign = b.align;
  if (b.leading) s.lineHeight = b.leading;
  return s;
};
